import _ from 'lodash'
import { ISSRContext } from 'ssr-types'
import { RouteLocationNormalizedLoaded } from 'vue-router'
import { IRequestExtension } from '~/src/interface'
import { IResponseData } from '~/typings'
import {
  UserInfo,
  IFooterConfigItem,
  ISeoItem,
  SiteLanguageItem,
  IDictItem,
  LanguageItem,
  CountryItem,
  I18nItem,
  ICourseListItem,
  UserClassroomItem,
  IHomeCourseItem
} from '@/interface'
// import { IApiServie } from '~/typings'
import { ApiService } from '~/src/modules/index/index.service'
import {
  getSiteInfo,
  getMultiLang,
  getUserInfo,
  getFooterConfig,
  getSeoConfigBySite,
  getSiteLanguageConfig,
  getCertificationKindList,
  getDictModelList,
  getCountryLangList,
  getSiteInfoByIds,
  getCountryByIp,
  getUserComplateProject,
  getRecommendedCourse,
  getRecommendedClass
} from '@/request/api/common'
import type { Request } from 'express'
import { getMessages } from '@/i18n'
import { getTenantId, CookieUtil } from '@/utils'

interface BaseSiteInfo {
  baseLanguageId: number
  nonCertified: string
  orderFood: string
  languageNavigation: string
}

export default async ({ store, router }: AsyncParams, ctx?: ISSRContext<{
  request: Request & { extension: IRequestExtension }
  apiService: ApiService
}>): Promise<void> => {
  let result: IResponseData[] = []
  let isoCode = ''
  const dictTypeList = [
    'front_logo',
    'certificate_step',
    'certification_programs',
    'learn_ways'
  ]
  let tenantId = ''
  // 测试ip
  // 195.80.122.213 爱沙尼亚
  // 82.198.3.138 立陶宛
  // 84.237.242.226 拉脱维亚
  // 62.133.194.69 荷兰
  // 1.32.239.254 美国
  // 45.5.53.255 墨西哥
  // 1.0.127.255 日本
  // 2.15.255.255 法国
  // 2.57.224.0 西班牙
  // const ipConfig = {
  //   ip
  // }
  const fullPath: string = ctx?.request?.extension?.fullPath ||
    (router as RouteLocationNormalizedLoaded & { href: string }).href
  if (store.state.global.dataLoaded) return
  let languageCheckedId = ''
  if (__isBrowser__) {
    languageCheckedId = CookieUtil.getCookie('languageCheckedId') as string
    const prefix = window.prefix
    // siteInfo要先存下来，供后续接口使用
    const siteInfo = await getSiteInfo({ siteName: prefix })
    window.siteId = (siteInfo.data as { siteCode: string, siteId: number }).siteId || 1
    tenantId = getTenantId(location.hostname)
    store.commit('global/setTenantId', tenantId)
    store.commit('global/setDefaultTitle', tenantId)
    result = await Promise.all([
      getSeoConfigBySite(),
      await Promise.resolve(siteInfo),
      getUserInfo(),
      getFooterConfig(),
      getSiteLanguageConfig(),
      getCertificationKindList(),
      getDictModelList({ dictTypeList }),
      getCountryLangList()
    ])
    const ipInfo = await getCountryByIp()
    isoCode = (ipInfo?.data as { iso_code: string })?.iso_code || ''
  } else {
    languageCheckedId = ctx?.request?.extension?.cookies?.languageCheckedId as string
    tenantId = getTenantId(ctx?.request?.extension?.hostname || '')
    store.commit('global/setTenantId', tenantId)
    store.commit('global/setDefaultTitle', tenantId)
    result = await Promise.all([
      ctx?.apiService.getSeoConfigBySite(),
      ctx?.apiService.getSiteInfo(),
      ctx?.apiService.getUserInfo(),
      ctx?.apiService.getFooterConfig(),
      ctx?.apiService.getSiteLanguageConfig(),
      ctx?.apiService.getCertificationKindList(),
      ctx?.apiService.getDictModelList({ dictTypeList }),
      ctx?.apiService.getCountryLangList()
    ])
    const ipInfo = await ctx?.apiService.getUserIpInfo({
      ip: ctx?.request?.extension?.userIp || null
    })
    isoCode = (ipInfo?.data as { iso_code: string })?.iso_code || ''
  }
  store.commit('global/setIsoCode', isoCode)
  if (result[0]) {
    store.dispatch('global/setSeoConfig', { config: (result[0]?.data || []) as ISeoItem[] })
  }
  if (result[1]) {
    store.commit('global/setSiteInfo', result[1]?.data as { siteCode: string, siteId: number })
  }
  if (result[2]?.code === 200) {
    const userInfo = result[2]?.data as UserInfo
    store.commit('global/setUserInfo', userInfo)
    // 如果已登录，则获取待学信息
    if (userInfo) {
      if (__isBrowser__) {
        const res = await getUserComplateProject()
        if (res?.code === 200) {
          store.commit('user/setProcessingCourseInfo', res.data as {
            classroomList: UserClassroomItem[]
            userCourseList: ICourseListItem[]
          })
        }
      } else {
        const res = await ctx?.apiService.listUserIncompleteProject()
        if (res?.code === 200) {
          store.commit('user/setProcessingCourseInfo', res.data as {
            classroomList: UserClassroomItem[]
            userCourseList: ICourseListItem[]
          })
        }
      }
    }
  }
  if (result[3]?.code === 200) {
    const footerConfig = result[3]?.data as IFooterConfigItem[]
    store.dispatch('global/setFooterConfig', { footerConfig })
  }
  if (result[4]?.code === 200) {
    const siteLanguageConfig = result[4]?.data as SiteLanguageItem[]
    store.dispatch('global/setSiteLanguageConfig', { siteLanguageConfig })
  }
  if (result[5]?.code === 200) {
    store.commit('global/setCertificationTypes', result[5]?.data as Array<{ kindType: string, kindName: string }>)
  }
  if (result[6]?.code === 200) {
    store.commit('global/setDictItemList', result[6]?.data as IDictItem[])
  }
  if (result[7]?.code === 200) {
    const languageList = Object.values(result[7]?.data as { key: string, value: LanguageItem[] }).flat()
    const countryList = Object.entries(result[7]?.data as { key: string, value: LanguageItem[] }).map(([key, value]) => ({
      country: key,
      languageList: [...value]
    }))
    store.commit('global/setLanguageList', languageList as LanguageItem[])
    store.commit('global/setCountryList', countryList as CountryItem[])
  }
  // 获取站点信息，主要是base语言
  let siteFetchData = null
  const siteId = (result[1]?.data as { siteCode: string, siteId: number })?.siteId || 1
  if (__isBrowser__) {
    siteFetchData = await getSiteInfoByIds({ id: siteId })
  } else {
    siteFetchData = await ctx?.apiService.getSiteInfoByIds({ id: siteId })
  }
  const siteInfo = siteFetchData ? (siteFetchData.data as BaseSiteInfo[])[0] : {} as BaseSiteInfo

  // --------------------
  // i18n语言获取，依赖前面的接口
  const isLogin = store.getters['global/isLogin']
  let i18nFetchData = null
  // 有些场景后端返回[null]
  const langList = (store.state.global.languageList || []).filter(Boolean) as LanguageItem[]
  let langIndex = -1
  /**
   * 语言初始化顺序，优先度 个人偏好-浏览器缓存-ip/浏览器语言-base语言
   * 1、如果用户已登录，读取用户个人偏好
   * 2、如果用户已登录，但是个人偏好为空，读取浏览器缓存
   * 3、如果用户未登录，读取浏览器缓存
   * 4、如果用户未登录，且无浏览器缓存，查看用户ip是否能匹配到
   * 4.1、能匹配到，检查ip国家下是否有用户浏览器语言，如果有用户浏览器语言，则取该语言
   * 4.2、能匹配到，检查ip国家下是否有用户浏览器语言，如果没有则取该国家下第一个语言
   * 5、ip未匹配到，读取站点base语言
   * PS：如果上面获取的语言偏好在本站点不存在，读取站点base语言
   */
  const langItemWithIpAndClientLangIndex = langList.findIndex(i => i.countryCode === isoCode && i.languageSimple?.toLowerCase() === ctx?.request?.extension?.clientLanguage)
  const langItemWithIpIndex = langList.findIndex(i => i.countryCode === isoCode)
  if (isLogin && store.state.global.userInfo.languageId) {
    langIndex = langList.findIndex(i => i.id === store.state.global.userInfo.languageId)
  } else if (languageCheckedId) {
    langIndex = langList.findIndex(i => i.id === Number(languageCheckedId))
  } else if (langItemWithIpAndClientLangIndex >= 0) {
    langIndex = langItemWithIpAndClientLangIndex
  } else if (langItemWithIpIndex >= 0) {
    langIndex = langItemWithIpIndex
  }
  if (langIndex === -1) {
    langIndex = langList.findIndex(i => i.id === siteInfo.baseLanguageId)
  }
  const languagePackId = langList[langIndex] ? langList[langIndex].languagePackId : 1
  const id = langList[langIndex] ? langList[langIndex].id : 1
  // 根据上面的国家偏好，获取i18n语言
  if (__isBrowser__) {
    i18nFetchData = await getMultiLang({ languagePackId })
    const courseListRes = await getRecommendedCourse({ languageId: id })
    const classroomListRes = await getRecommendedClass({ languageId: id })
    store.commit('common/seCourseList', (courseListRes?.code === 200 ? courseListRes?.data : []) as IHomeCourseItem[])
    store.commit('common/setClassroomList', (classroomListRes?.code === 200 ? classroomListRes?.data : []) as UserClassroomItem[])
  } else {
    i18nFetchData = await ctx?.apiService.getI18nConfig({ languagePackId })
    const courseListRes = await ctx?.apiService.getRecommendedCourse({ languageId: id })
    const classroomListRes = await ctx?.apiService.getRecommendedClass({ languageId: id })
    store.commit('common/seCourseList', (courseListRes?.code === 200 ? courseListRes?.data : []) as IHomeCourseItem[])
    store.commit('common/setClassroomList', (classroomListRes?.code === 200 ? classroomListRes?.data : []) as UserClassroomItem[])
  }
  const i18nData = (i18nFetchData?.data || []) as I18nItem[]
  if (i18nFetchData?.code === 200 && Array.isArray(i18nData)) {
    const messages = {
      en: {} as Record<string, string>
    }
    i18nData.forEach((item: I18nItem) => {
      messages.en[item.textCode] = item.textName
    })
    const localeMessage = getMessages(tenantId)
    store.commit('global/setI18n', _.merge({}, localeMessage, messages))
  }
  store.commit('global/setLangugeKey', id)
  if (__isBrowser__) {
    window.languageId = id
  } else if (ctx?.request?.extension) {
    ctx.request.extension.languageId = id + ''
  }
  // 检查当前的语言是否是阿语
  store.commit('global/setIsArSite', langList[langIndex] ? langList[langIndex].rtlStatus === '1' : false)
  // store.commit('global/setIsArSite', true)
  // --------------------
  store.commit('global/setDataLoaded', true)
  store.commit('global/setIsEU', new RegExp(/^\/eu/).test(fullPath))
  store.commit('global/setIsAsia', new RegExp(/^\/asia/).test(fullPath))
  store.commit('global/setShowNonCertiMenu', siteInfo.nonCertified === '1')
  store.commit('global/setOrderFoodDisplay', siteInfo.orderFood === '1')
  // 是否显示 Preferences
  store.commit('global/setShowPreferences', siteInfo.languageNavigation === '1')
}
